import { GeoJsonPoint } from '@/interfaces/misc.interface'
import {
  getRequestGateway,
  patchRequestGateway,
  postRequestGateway,
  deleteRequestGateway
} from '@/services/api.service'

// Interfaces

export type Comment = {
  id: string
  createdDate: string
  lastModifiedDate: string
  realmId: string
  ticketId: string
  comment: string
  createdBy: string
  edited: boolean
}
type CommentDto = {
  comment: string
}
export type CreateTicket = {
  externalId?: string | null
  title: string
  description?: string | null
  categoryId: string
  typeId: string
  entityConnection: EntityConnection[]
  priority: TicketPriority // TicketPriority.LOWEST default if nothing chosen,
  status: TicketStatus // TicketStatus.NOT_IN_PROGRESS default if nothing chosen,
  startDate?: string | null
  dueDate?: string | null
  assignee?: string | null
}

export type EntityConnection = {
  id: string
  entityType: EntityType
}

type EntityType = 'REAL_ESTATE' | 'CONSTRUCTION' | 'LEVEL' | 'SPACE' | 'ZONE' | 'LINE' | 'POI'
type LangCode = {
  sv: string
  en: string
}
type Patch<T> = {
  to?: T
  from?: T | null
}
type TicketPriority = 'HIGHEST' | 'HIGH' | 'MEDIUM' | 'LOW' | 'LOWEST'
type TicketStatus = 'ON_GOING' | 'ON_HOLD' | 'NOT_IN_PROGRESS' | 'FINISHED' | 'CANCELLED'

export type Ticket = {
  id: string
  realmId: string
  externalId?: string
  ticketNumber: number | null
  title: string
  description?: string
  categoryId: string
  typeId: string
  entityConnection: EntityConnection[]
  priority: TicketPriority
  status: TicketStatus
  dueDate?: string
  finishedDate?: string
  createdBy: string | null
  assignee?: string
  lastModifiedBy?: string | null
  revision: number
  active: boolean // true is default
  createdDate: string
  lastModifiedDate: string
}

export type TicketCategory = {
  id: string
  realmId: string
  createdDate: string
  lastModifiedDate: string
  name: LangCode
  types: TicketType[]
}

export type TicketPatch = {
  id: string
  externalId?: Patch<string> | null
  finishedDate?: Patch<string> | null
  ticketId: string
  realmId: string
  revision: number // val revision: Int = -1,
  title?: Patch<string> | null
  description?: Patch<string> | null
  categoryId?: Patch<string> | null
  typeId?: Patch<string> | null
  priority?: Patch<string> | null
  status?: Patch<string> | null
  startDate?: Patch<string> | null
  dueDate?: Patch<string> | null
  createdBy: string
  assignee?: Patch<string> | null
  createdDate?: string
}

export type TicketType = {
  id: string
  realmId: string
  createdDate: string
  lastModifiedDate: string
  name: LangCode
}

export type UpdateTicket = {
  externalId?: string | null
  title?: string | null
  description?: string | null
  categoryId?: string | null
  typeId?: string | null
  priority?: TicketPriority | null
  status?: TicketStatus | null
  startDate?: string | null
  dueDate?: string | null
  assignee?: string | null
}

export type TicketQueryDto = {
  entityId: string
  externalId?: string | null
  priority?: TicketPriority | null
  status?: TicketStatus | null
  createdBy?: string | null
  categoryId?: string | null
  typeId?: string | null
  startFrom?: string | null
  startTo?: string | null
  dueFrom?: string | null
  dueTo?: string | null
  createdFrom?: string | null
  createdTo?: string | null
  finishedFrom?: string | null
  finishedTo?: string | null
  lastModifiedFrom?: string | null
  lastModifiedTo?: string | null
}

export type Page<T> = {
  content: T[]
  pageable: {
    pageNumber: number
    pageSize: number
    sort: { empty: boolean; unsorted: boolean; sorted: boolean }
    offset: number
    unpaged: boolean
    paged: boolean
  }
  totalPages: number
  totalElements: number
  last: boolean
  size: number
  number: number
  sort: { empty: boolean; unsorted: boolean; sorted: boolean }
  first: boolean
  numberOfElements: number
  empty: boolean
}

// Requests

export async function archiveTicket(ticketId: string): Promise<Ticket | undefined> {
  const endpoint = `${window.appConfig.VITE_API_GW_BASE_URL}/ticket/v1/tickets/${ticketId}/archive`
  const response = await patchRequestGateway(endpoint, null, null)
  return response?.data
}

export async function createComment(
  ticketId: string,
  comment: CommentDto
): Promise<Comment | undefined> {
  const endpoint = `${window.appConfig.VITE_API_GW_BASE_URL}/ticket/v1/tickets/${ticketId}/comments`
  const response = await postRequestGateway(endpoint, comment)
  return response?.data
}

export async function createTicket(createTicket: CreateTicket): Promise<Ticket | undefined> {
  const endpoint = `${window.appConfig.VITE_API_GW_BASE_URL}/ticket/v1/tickets`
  const response = await postRequestGateway(endpoint, createTicket)
  return response?.data
}

export async function getTicketTitles(
  ticketIds: string[]
): Promise<{ titles: { title: string; id: string }[] } | undefined> {
  const endpoint = `${window.appConfig.VITE_API_GW_BASE_URL}/ticket/v1/tickets/titles`
  const response = await postRequestGateway(endpoint, { ids: ticketIds })
  return response?.data
}

export async function deleteTicket(ticketId: string): Promise<number> {
  const endpoint = `${window.appConfig.VITE_API_GW_BASE_URL}/ticket/v1/tickets/${ticketId}`
  // no response body, just 200
  const response = await deleteRequestGateway(endpoint)
  return response.status
}

export async function getSingleTicket(ticketId: string): Promise<Ticket | undefined> {
  const endpoint = `${window.appConfig.VITE_API_GW_BASE_URL}/ticket/v1/tickets/${ticketId}`
  const response = await getRequestGateway<Ticket>(endpoint)
  return response?.data
}

// Get all ticket categories in realm
export async function getTicketCategories(): Promise<TicketCategory[] | undefined> {
  const endpoint = `${window.appConfig.VITE_API_GW_BASE_URL}/ticket/v1/categories`
  const response = await getRequestGateway<TicketCategory[]>(endpoint)
  return response?.data
}

export async function getTicketCommentsPage(
  ticketId: string,
  page: number,
  size: number
): Promise<Page<Comment> | undefined> {
  const endpoint = `${window.appConfig.VITE_API_GW_BASE_URL}/ticket/v1/tickets/${ticketId}/comments?page=${page}&size=${size}`
  const response = await getRequestGateway<Page<Comment>>(endpoint)
  return response?.data
}

export async function getTicketHistoryPage(
  ticketId: string,
  page: number,
  size: number
): Promise<Page<TicketPatch> | undefined> {
  const endpoint = `${window.appConfig.VITE_API_GW_BASE_URL}/ticket/v1/tickets/${ticketId}/history?page=${page}&size=${size}`
  const response = await getRequestGateway<Page<TicketPatch>>(endpoint)
  return response?.data
}

export async function getTicketsPage(
  withChildren: boolean,
  withFinished: boolean,
  getArchived: boolean,
  page: number,
  size: number,
  ticketsQuery: TicketQueryDto,
  sort: string = 'priority,desc',
  signal: AbortSignal | undefined = undefined
): Promise<Page<Ticket> | undefined | 'canceled'> {
  /*
  For sorting, write as RequestParam sort=<ticket field name>,asc or <ticket field name>,desc.
  For example: /v1/tickets/page?page=0&size=10&sort=status,desc
  */
  const endpoint = `${window.appConfig.VITE_API_GW_BASE_URL}/ticket/v1/tickets/page?withChildren=${withChildren}&withFinished=${withFinished}&getArchived=${getArchived}&page=${page}&size=${size}&sort=${sort}`
  try {
    const response = await postRequestGateway(endpoint, ticketsQuery, signal)
    return response?.data
  } catch (error: any) {
    if (error.message === 'canceled') {
      return 'canceled'
    }
    return undefined
  }
}

export async function getTicketsPageForUser(
  page: number,
  size: number
): Promise<Page<Ticket> | undefined> {
  const endpoint = `${window.appConfig.VITE_API_GW_BASE_URL}/ticket/v1/tickets/user?page=${page}&size=${size}`
  const response = await getRequestGateway<Page<Ticket>>(endpoint) // possibly add timeout and retries
  return response?.data
}

export async function getTicketType(typeId: string): Promise<TicketType | undefined> {
  const endpoint = `${window.appConfig.VITE_API_GW_BASE_URL}/ticket/v1/types/${typeId}`
  const response = await getRequestGateway<TicketType>(endpoint)
  return response?.data
}

// Get all ticket categories in realm
export async function getTicketTypesForCategory(
  categoryId: string
): Promise<TicketType[] | undefined> {
  const endpoint = `${window.appConfig.VITE_API_GW_BASE_URL}/ticket/v1/types/${categoryId}/category`
  const response = await getRequestGateway<TicketType[]>(endpoint)
  return response?.data
}

export async function updateTicket(
  ticketId: string,
  updateTicket: UpdateTicket
): Promise<Ticket | undefined> {
  const endpoint = `${window.appConfig.VITE_API_GW_BASE_URL}/ticket/v1/tickets/${ticketId}`
  const response = await patchRequestGateway(endpoint, updateTicket, null)
  return response?.data
}

// Remaining endpoints for categories and types below:
/*
POST v1/categories (RequestBody: CreateTicketCategory)
response: TicketCategory

GET v1/categories/{id}
reponse: TicketCategory

DELETE v1/categories/{id}
no response body, status 204
*/

/*
Comments:

PATCH v1/tickets/{id}/comments (Update comment by id)
RequestBody: CommentDto
response: Comment
*/

// Grouped tickets types – used for ticket markers on map
export type GroupedResponseDto = {
  entityId: string
  entityName: string
  type: EntityType
  location?: GeoJsonPoint
  tickets: GroupedResponseTicketDto[]
  summary: { [key: string]: number } // val summary: Map<String, Int>,
}

type GroupedResponseTicketDto = {
  id: string
  type: string
  priority: TicketPriority
  status: TicketStatus
}

// Included in GroupedResponseDto summary
export type TypeWithPriority = {
  type: string
  highestPriority: TicketPriority
  countTickets: number
}

// Grouped tickets requests – used for ticket markers on map
export async function getGroupedTicketsForEntity(
  entityId: string,
  splitBy?: EntityType,
  signal: AbortSignal | undefined = undefined
): Promise<GroupedResponseDto[] | undefined | 'canceled'> {
  let endpoint = `${window.appConfig.VITE_API_GW_BASE_URL}/ticket/v1/tickets/${entityId}/grouped`
  if (splitBy) endpoint += `?splitBy=${splitBy}`
  // RequestParam(required = false) langCode:LangCode = LangCode.SV (not prepared on backend when this was written)
  try {
    const response = await getRequestGateway<GroupedResponseDto[]>(endpoint, 0, 1, {}, signal)
    return response?.data
  } catch (error: any) {
    if (error.message === 'canceled') {
      return 'canceled'
    }
    return undefined
  }
}
